import illustrationUrl from '../assets/barbichette.svg';
import { h, mount } from './dom';

import type { GameKind, GameMode } from '../game/protocol';
import type { SignalingState, SignalingStatus } from '../net/signaling';

const SIGNALING_TEXT: Record<SignalingState, string> = {
  online: 'Prêt : ton ami peut ouvrir le lien.',
  reconnecting: 'Reconnexion au serveur… garde cette page ouverte.',
  stopped: 'Connexion au serveur fermée : recharge la page pour créer une nouvelle partie.',
};

/** Choix « le premier qui rit / cligne » (l'hôte décide, avant l'arrivée de l'ami). */
export function modeChoice(
  initial: GameMode,
  onChange: (mode: GameMode) => void,
  prefix = 'mode',
): HTMLElement {
  const option = (mode: GameMode, label: string) => {
    const input = h('input', {
      type: 'radio',
      name: `${prefix}-choice`,
      value: mode,
      testid: `${prefix}-${mode}`,
    });
    input.checked = mode === initial;
    input.addEventListener('change', () => {
      onChange(mode);
    });
    return h('label', {}, input, label);
  };
  return h(
    'fieldset',
    { class: 'segmented' },
    h('legend', {}, 'Le premier qui…'),
    option('laugh', 'rit'),
    option('blink', 'cligne'),
  );
}

/** Choix du jeu : barbichette (rire / clignement) ou chifoumi. */
export interface LobbyChoice {
  readonly game: GameKind;
  readonly mode: GameMode;
}

function gameChoice(initial: GameKind, onChange: (game: GameKind) => void): HTMLElement {
  const option = (game: GameKind, label: string) => {
    const input = h('input', {
      type: 'radio',
      name: 'lobby-game-choice',
      value: game,
      testid: `lobby-game-${game}`,
    });
    input.checked = game === initial;
    input.addEventListener('change', () => {
      onChange(game);
    });
    return h('label', {}, input, label);
  };
  return h(
    'fieldset',
    { class: 'segmented' },
    h('legend', {}, 'Jeu'),
    option('barbichette', 'Barbichette'),
    option('chifoumi', 'Chifoumi'),
    option('smiley', 'Imite ce smiley'),
  );
}

export function renderHostLobby(
  root: HTMLElement,
  link: string,
  initial: LobbyChoice,
  onChange: (choice: LobbyChoice) => void,
  signaling?: SignalingStatus,
  onCancel?: () => void,
): () => void {
  let choice = initial;
  const modes = modeChoice(
    choice.mode,
    (mode) => {
      choice = { ...choice, mode };
      onChange(choice);
    },
    'lobby-mode',
  );
  modes.hidden = choice.game !== 'barbichette';
  const games = gameChoice(choice.game, (game) => {
    choice = { ...choice, game };
    modes.hidden = game !== 'barbichette';
    onChange(choice);
  });
  const input = h('input', {
    readonly: '',
    value: link,
    testid: 'invite-link',
    'aria-label': "Lien d'invitation",
  });
  input.addEventListener('focus', () => {
    input.select();
  });
  const signalingLine = h('p', { class: 'muted', testid: 'signaling-status', role: 'status' });
  const showSignaling = (state: SignalingState): void => {
    signalingLine.textContent = SIGNALING_TEXT[state];
    signalingLine.classList.toggle('error', state !== 'online');
  };
  if (signaling) showSignaling(signaling.state);
  const offSignaling = signaling?.onChange(showSignaling);
  const cancel = onCancel
    ? h('button', { type: 'button', class: 'button secondary', testid: 'cancel' }, 'Annuler')
    : null;
  if (cancel && onCancel) cancel.addEventListener('click', onCancel);
  const copy = h('button', { type: 'button', class: 'button secondary' }, 'Copier le lien');
  copy.addEventListener('click', () => {
    void navigator.clipboard.writeText(link).then(() => {
      copy.textContent = 'Copié !';
    });
  });
  mount(
    root,
    h('h2', {}, 'Envoie ce lien à ton ami'),
    h(
      'div',
      { class: 'card' },
      h(
        'p',
        { class: 'muted' },
        'Par SMS, messagerie, pigeon voyageur… Dès qu’il l’ouvre, la partie commence.',
      ),
      h('div', { class: 'invite' }, input, copy),
    ),
    games,
    modes,
    h('img', {
      class: 'illustration small',
      src: illustrationUrl,
      alt: '',
      width: '200',
      height: '200',
    }),
    h('p', { class: 'status waiting', testid: 'status', role: 'status' }, 'En attente de ton ami'),
    h('p', { class: 'muted' }, 'Garde cette page ouverte jusqu’à l’arrivée de ton ami.'),
    signalingLine,
    cancel,
  );
  return () => offSignaling?.();
}

export interface MessageAction {
  readonly label: string;
  readonly testid: string;
  readonly onClick: () => void;
  readonly secondary?: boolean;
}

export function renderMessage(
  root: HTMLElement,
  text: string,
  kind: 'info' | 'error' = 'info',
  actions: readonly MessageAction[] = [],
): void {
  const buttons = actions.map((a) => {
    const b = h(
      'button',
      { type: 'button', class: a.secondary ? 'button secondary' : 'button', testid: a.testid },
      a.label,
    );
    b.addEventListener('click', a.onClick);
    return b;
  });
  mount(
    root,
    h(
      'p',
      { class: kind === 'error' ? 'status error' : 'status', testid: 'status', role: 'status' },
      text,
    ),
    buttons.length > 0 ? h('div', { class: 'actions' }, ...buttons) : null,
  );
}
